import dayjs from 'dayjs';
import axios from './axios';

import { PAGE_SIZE } from '../constants/auth';
import { formatDateWithOrdinal } from '../utils/helpers';
import {
  ApiDocumentSection,
  Compliance,
  ComplianceServices,
  ComplianceUpdate,
  DocumentSection,
  StripeCustomer,
  StripeSubscription,
  Document,
  ComplianceInList,
  ComplianceStatus,
  complianceStripeStatusMap,
  ComplianceRequest,
  ComplianceBillingFrequency,
} from '../types/compliance';
import { Language, PaginatedResponse } from './types';
import { ProductStatus } from '../types/companyUser';

const getComplianceInformation = (apiResponse: any) => {
  const compliance: Compliance = {} as Compliance;
  const apiStatus = apiResponse?.stripeSubscriptionData?.status;
  compliance.id = apiResponse?.id;
  compliance.company = apiResponse?.company.id;
  compliance.billingConfig = apiResponse?.billingConfig
    ? {
        fee: apiResponse.billingConfig.fee,
        frequency: apiResponse.billingConfig.frequency,
        firstChargeMonth: apiResponse.billingConfig.firstChargeMonth,
        lastPaidAt: apiResponse.billingConfig.lastPaidAt,
        nextChargeDate: apiResponse.billingConfig.nextChargeDate,
        recurringChargeId: apiResponse.billingConfig.recurringChargeId,
      }
    : null;
  if (compliance.billingConfig?.recurringChargeId) {
    compliance.status = ComplianceStatus.ACTIVE;
  } else {
    compliance.status =
      apiStatus && apiStatus in complianceStripeStatusMap
        ? complianceStripeStatusMap[apiStatus]
        : ComplianceStatus.NOT_CONNECTED;
  }
  compliance.planType = apiResponse?.planType;
  compliance.includedServices = apiResponse?.includedServices;
  compliance.cpa = apiResponse?.cpa;
  compliance.cpaOther = apiResponse?.cpaOther;
  compliance.stripeSubscription = apiResponse?.stripeSubscriptionData
    ? getSubscriptionInformation(apiResponse?.stripeSubscriptionData)
    : null;
  compliance.connectedToStripe = !!apiResponse?.connectedToStripe;
  return compliance;
};

const getComplianceListInformation = (apiResponse: PaginatedResponse<any>) => {
  const complianceResults: ComplianceInList[] = [];
  apiResponse.results.forEach((result) => {
    const apiStatus = result?.stripeSubscriptionData?.status;
    const compliance: ComplianceInList = {} as ComplianceInList;
    compliance.id = result.id;
    compliance.company = result.company.id;
    compliance.companyName = result.company.name;
    compliance.companyUUID = result.company.uuid;
    compliance.cpa = { cpa: result.cpa, other: result.cpaOther };
    compliance.includedServices = result.includedServices;
    compliance.members = result.company.users;
    compliance.price = (result?.stripeSubscriptionData?.plan?.amount || 0) / 100;
    compliance.currency = result?.stripeSubscriptionData?.plan?.currency;
    compliance.status =
      apiStatus && apiStatus in complianceStripeStatusMap
        ? complianceStripeStatusMap[apiStatus]
        : ComplianceStatus.NOT_CONNECTED;
    complianceResults.push(compliance);
  });
  return { ...apiResponse, results: complianceResults };
};

const getSubscriptionInformation = (apiSubscription: any) => {
  const subscription: StripeSubscription = {} as StripeSubscription;
  subscription.id = apiSubscription.id;
  subscription.name = apiSubscription?.plan?.product?.name;
  subscription.price = (apiSubscription?.plan?.amount || 0) / 100;
  subscription.currency = apiSubscription?.plan?.currency;
  subscription.frecuency = apiSubscription?.plan?.interval;
  subscription.dueDate = apiSubscription?.currentPeriodEnd
    ? formatDateWithOrdinal(dayjs.unix(apiSubscription?.currentPeriodEnd))
    : undefined;
  subscription.methodAssociatedFunding = apiSubscription?.defaultPaymentMethod?.card?.funding;
  subscription.methodAssociatedNumbers = apiSubscription?.defaultPaymentMethod?.card?.last4;
  return subscription;
};

const getSectionInformation = (apiSection: any, language: Language) => {
  const section: DocumentSection = {} as DocumentSection;
  section.id = apiSection.id;
  section.name = language === Language.EN ? apiSection.nameEn : apiSection.nameEs;
  section.description = language === Language.EN ? apiSection.descriptionEn : apiSection.descriptionEs;
  section.default = apiSection.default;
  section.type = apiSection.groupingType;
  section.product = apiSection.product;
  section.notificationDate = apiSection.notificationDate;
  section.documents = apiSection?.documents?.map((document: any) => getDocumentInformation(document)) || [];
  return section;
};

const getDocumentInformation = (apiDocument: any, language?: Language) => {
  const document: Document = {} as Document;
  document.id = apiDocument.id;
  document.year = apiDocument.year;
  document.month = apiDocument.month;
  document.assetAmount = apiDocument.assets.length;
  document.assetNames = apiDocument.assets?.map((asset: any) => asset.fileName)?.join(', ');
  document.sectionName = language === Language.EN ? apiDocument.sectionNameEn : apiDocument.sectionNameEs;
  document.sectionDescription =
    language === Language.EN ? apiDocument.sectionDescriptionEn : apiDocument.sectionDescriptionEs;
  document.sectionProduct = apiDocument.sectionProduct;
  return document;
};

// Compliance
export async function getCompliance(companyId: number): Promise<Compliance> {
  const compliance = await axios
    .get(`/admins/companies/${companyId}/products/compliance/`)
    .then(({ data }) => getComplianceInformation(data));

  return compliance;
}

export async function initializeCompliance(companyId: number): Promise<Compliance> {
  const compliance = await axios
    .post(`/admins/companies/${companyId}/products/compliance/`)
    .then(({ data }) => getComplianceInformation(data));

  return compliance;
}

export async function updateCompliance(
  companyId: number,
  complianceId: number,
  values: ComplianceUpdate
): Promise<Compliance> {
  const compliance = await axios
    .patch(`/admins/companies/${companyId}/products/compliance/${complianceId}/`, values)
    .then(({ data }) => getComplianceInformation(data));

  return compliance;
}

export async function startCompliance(companyId: number, productStatusId: number): Promise<ProductStatus> {
  return axios
    .patch(`/companies/${companyId}/products/product_status/${productStatusId}/`, {
      hasStartedCompliance: true,
    })
    .then(({ data }) => data);
}

export async function toggleReadyForCompliance(companyId: number): Promise<ProductStatus> {
  return await axios
    .post(`/admins/companies/${companyId}/products/compliance/toggle_ready_for_compliance/`)
    .then(({ data }) => data);
}

// Stripe
export async function searchStripeCustomers(clientName: string): Promise<StripeCustomer[]> {
  const customersInformation = await axios
    .get(`/admins/products/compliance/stripe_customers/?query=${clientName}`)
    .then(({ data }) => data);
  return (
    customersInformation?.data.map((customer: any) => ({
      id: customer?.id,
      name: customer?.name,
    })) || []
  );
}

export async function getStripeSubscriptions(customerId: string): Promise<StripeSubscription[]> {
  const subscriptionsInfo = await axios
    .get(`/admins/products/compliance/stripe_customers/${customerId}/subscriptions/`)
    .then(({ data }) => data);
  return subscriptionsInfo?.data.map((subscription: any) => getSubscriptionInformation(subscription)) || [];
}

export async function integrateComplianceWithStripe(
  companyId: number,
  complianceId: number,
  customerId: string,
  subscriptionId: string
): Promise<Compliance> {
  const compliance = await axios
    .post(`/admins/companies/${companyId}/products/compliance/${complianceId}/integration/`, {
      customer_id: customerId,
      subscription_id: subscriptionId,
    })
    .then(({ data }) => getComplianceInformation(data));

  return compliance;
}

export async function removeIntegrationWithStripe(
  companyId: number,
  complianceId: number
): Promise<Compliance> {
  const compliance = await axios
    .delete(`/admins/companies/${companyId}/products/compliance/${complianceId}/integration/`)
    .then(({ data }) => getComplianceInformation(data));

  return compliance;
}

export async function createComplianceBillingConfig(
  companyId: number,
  complianceId: number,
  fee: string,
  frequency: ComplianceBillingFrequency,
  firstChargeMonth: string,
  replaceStripe = false
): Promise<Compliance> {
  return axios
    .post(`/admins/companies/${companyId}/products/compliance/${complianceId}/billing_config/`, {
      fee,
      frequency,
      first_charge_month: firstChargeMonth,
      replace_stripe: replaceStripe,
    })
    .then(({ data }) => getComplianceInformation(data));
}

export async function updateComplianceBillingConfig(
  companyId: number,
  complianceId: number,
  fee: string
): Promise<Compliance> {
  return axios
    .patch(`/admins/companies/${companyId}/products/compliance/${complianceId}/billing_config/`, {
      fee,
    })
    .then(({ data }) => getComplianceInformation(data));
}

export async function removeComplianceBillingConfig(
  companyId: number,
  complianceId: number
): Promise<Compliance> {
  return axios
    .delete(`/admins/companies/${companyId}/products/compliance/${complianceId}/billing_config/`)
    .then(({ data }) => getComplianceInformation(data));
}

// Document Sections
export async function getDocumentSections(
  companyId: number,
  complianceId: number,
  language: Language
): Promise<DocumentSection[]> {
  const sections = await axios
    .get(`/admins/companies/${companyId}/products/compliance/${complianceId}/document_sections/`)
    .then(({ data }) => data);
  return sections.map((section: any) => getSectionInformation(section, language));
}

export async function downloadSectionDocuments(
  companyId: number,
  complianceId: number,
  service: ComplianceServices
) {
  return await axios
    .get(
      `/admins/companies/${companyId}/products/compliance/${complianceId}/document_sections/download/?product=${service}`,
      {
        responseType: 'blob',
      }
    )
    .then(({ data }) => data);
}

export async function addDocumentSection(
  companyId: number,
  complianceId: number,
  values: ApiDocumentSection,
  language: Language
): Promise<DocumentSection> {
  const newSection = await axios
    .post(`/admins/companies/${companyId}/products/compliance/${complianceId}/document_sections/`, values)
    .then(({ data }) => data);
  return getSectionInformation(newSection, language);
}

export async function deleteDocumentSection(
  companyId: number,
  complianceId: number,
  documentSectionId: number
): Promise<DocumentSection> {
  const deletedSection = await axios
    .delete(
      `/admins/companies/${companyId}/products/compliance/${complianceId}/document_sections/${documentSectionId}/`
    )
    .then(({ data }) => getSectionInformation(data, Language.EN));

  return deletedSection;
}

// Documents
export async function downloadDocuments(
  companyId: number,
  complianceId: number,
  documentSectionId: number,
  documentId: number
) {
  return await axios
    .get(
      `/admins/companies/${companyId}/products/compliance/${complianceId}/document_sections/${documentSectionId}/documents/${documentId}/download/`,
      { responseType: 'blob' }
    )
    .then((response) => response);
}

export async function addDocument(
  companyId: number,
  complianceId: number,
  documentSectionId: number,
  values: FormData,
  language: Language
): Promise<DocumentSection> {
  const newSection = await axios
    .post(
      `/admins/companies/${companyId}/products/compliance/${complianceId}/document_sections/${documentSectionId}/documents/`,
      values,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    .then(({ data }) => data);
  return getSectionInformation(newSection, language);
}

export async function deleteDocument(
  companyId: number,
  complianceId: number,
  sectionId: number,
  documentId: number,
  language: Language
): Promise<DocumentSection> {
  return await axios
    .delete(
      `/admins/companies/${companyId}/products/compliance/${complianceId}/document_sections/${sectionId}/documents/${documentId}/`
    )
    .then(({ data }) => getSectionInformation(data, language));
}

export async function notifyUserDocumentSection(
  companyId: number,
  complianceId: number,
  documentSectionId: number
) {
  return await axios
    .post(
      `/admins/companies/${companyId}/products/compliance/${complianceId}/document_sections/${documentSectionId}/notify/`
    )
    .then(({ data }) => data);
}

export async function notifyUserDocument(
  companyId: number,
  complianceId: number,
  documentSectionId: number,
  documentId: number
) {
  return await axios
    .post(
      `/admins/companies/${companyId}/products/compliance/${complianceId}/document_sections/${documentSectionId}/documents/${documentId}/notify/`
    )
    .then(({ data }) => data);
}

// Compliance List
export async function getAllComplianceServices(
  pageNumber: number = 1,
  search?: string
): Promise<PaginatedResponse<ComplianceInList>> {
  const params = { pageSize: PAGE_SIZE, page: pageNumber, company: search };
  const complianceServicesResponse: PaginatedResponse<any> = await axios
    .get(`/admins/products/compliance/`, { params: params || {} })
    .then(({ data }) => data);

  return getComplianceListInformation(complianceServicesResponse);
}

export async function downloadReport(search: string) {
  return await axios
    .get(`/admins/products/compliance/report/?company=${search}`, { responseType: 'blob' })
    .then((response) => response);
}

// Company user side
export async function createComplianceRequest(
  companyId: number,
  requestType?: string,
  note?: string
): Promise<ComplianceRequest> {
  return await axios
    .post(`/companies/${companyId}/products/compliance/requests/`, {
      request_type: requestType ? `${requestType}_request` : 'service_request',
      note,
    })
    .then(({ data }) => data);
}

export async function getComplianceRequests(companyId: number): Promise<ComplianceRequest[]> {
  return await axios.get(`/companies/${companyId}/products/compliance/requests/`).then(({ data }) => data);
}

export async function getCompanyCompliance(companyId: number): Promise<Compliance> {
  const compliance = await axios
    .get(`/companies/${companyId}/products/compliance/`)
    .then(({ data }) => getComplianceInformation(data));

  return compliance;
}

export async function getDocuments(
  companyId: number,
  complianceId: number,
  language: Language,
  page: number,
  pageSize: number,
  filters: { search: string; type?: string } = { search: '', type: undefined }
): Promise<PaginatedResponse<Document>> {
  const params = { search: filters.search, product: filters.type, page, pageSize };
  let paginatedResponse = await axios
    .get(`/companies/${companyId}/products/compliance/${complianceId}/documents/`, { params: params || {} })
    .then(({ data }) => data);
  paginatedResponse = {
    ...paginatedResponse,
    results: paginatedResponse.results.map((document: any) => getDocumentInformation(document, language)),
  };
  return paginatedResponse;
}

export async function downloadDocumentsCompanyUser(
  companyId: number,
  complianceId: number,
  documentId: number
) {
  return await axios
    .get(`/companies/${companyId}/products/compliance/${complianceId}/documents/${documentId}/download/`, {
      responseType: 'blob',
    })
    .then((response) => response);
}
